import React, { useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';

import ApplicationRecord from '../../../records/application';
import BeneficialOwnerCreate from './BeneficialOwnerCreate';
import Error from '../../shared/Error';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { asyncActions } from '../../../actions/general';
import { getRoute, loading } from '../../../actions/sessions';
import { updateApplication } from '../../../actions/applications';
import CTAButton from '../../shared/CTAButton';

export default function BeneficialOwner({ location }) {
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const application = useSessionEntity('application');
  const user = useSessionEntity('user');
  const company = useSessionEntity('company');
  const [addFormShown, setAddFormShown] = useState(false);

  useRouteStatus(location);

  const OwnerDetails = (props) => {
    return (
      <div className="rounded-lg bg-white border border-border-gray shadow my-2">
        <div className="flex w-full items-center justify-between space-x-6 p-6">
          <div className="flex-1 truncate">
            <div className="flex justify-between items-center space-x-3">
              <h3 className="truncate text-sm font-avenir-medium text-gray-900">
                Owner #{props.owner_number}
              </h3>
              {props.owner_number > 1 ? (
                <>
                  <span className="inline-block flex-shrink-0 rounded-full bg-red-100 px-2 pt-1 py-0.5 text-xs font-avenir-medium text-negative-red">
                    <button
                      className="cursor-pointer"
                      data-email={props.owner_email}
                      onClick={props.removeOwner}
                    >
                      Remove
                    </button>
                  </span>
                </>
              ) : null}
            </div>
            <div className="mt-1 truncate text-sm text-gray-500">
              <p>{props.owner_name}</p>
              <p>{props.owner_email}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const save = (e) => {
    e.preventDefault();

    dispatch(loading(true));
    dispatch(
      asyncActions(
        updateApplication(
          new ApplicationRecord({
            ...application.toJS(),
            are_additional_owners_confirmed: true,
          }),
        ),
        getRoute(),
      ),
    ).then((res) => dispatch(loading(false)));
  };

  const toggleAddOwner = () => setAddFormShown(!addFormShown);

  const persistOwners = (ownerList) => {
    dispatch(loading(true));
    dispatch(
      asyncActions(
        updateApplication(
          new ApplicationRecord({
            ...application.toJS(),
            beneficial_owners: ownerList,
          }),
        ),
      ),
    )
      .then(() => addFormShown && toggleAddOwner())
      .then(() => dispatch(loading(false)));
  };

  const addOwner = (values) => {
    const temp = application.beneficial_owners.push(values);

    return persistOwners(temp);
  };

  const removeOwner = (e) => {
    const index = application.beneficial_owners.findIndex(
      (b) => b.get('email') === e.currentTarget.dataset.email,
    );
    const temp = application.beneficial_owners.delete(index);

    return persistOwners(temp);
  };

  return (
    <div id="content">
      <div className="text-center">
        <div className="text-2xl font-avenir-medium mb-5">
          Are there any other owners of
          <span className="whitespace-nowrap mx-1">{company.name}</span>?
        </div>
        <div className="font-avenir-medium text-positive-green">
          Please add any individuals with 25% or more ownership
        </div>
      </div>

      <Error />

      <div>
        {
          <OwnerDetails
            owner_number="1"
            owner_name={user.full_name}
            owner_email={user.get('email')}
          />
        }
      </div>

      {application.beneficial_owners.map((b, idx) => {
        return (
          <div key={idx + 2}>
            <div>
              <OwnerDetails
                owner_number={idx + 2}
                owner_name={b.full_name}
                owner_email={b.get('email')}
                removeOwner={removeOwner}
              />
            </div>
          </div>
        );
      })}

      <div
        className="my-3 w-full mobile:w-[40%] text-primary-blue cursor-pointer font-avenir-medium tracking-wider text-sm inline-block hover:text-navy-blue flex items-center"
        onClick={toggleAddOwner}
        role="add"
      >
        <FontAwesomeIcon icon="plus" className="mx-1.5 mb-1" />
        <p>Add Another Owner</p>
      </div>

      {addFormShown && (
        <BeneficialOwnerCreate
          toggleAddOwner={toggleAddOwner}
          addOwner={addOwner}
        />
      )}

      <div className="text-center">
        <p className="mb-4">
          I hereby certify, to the best of my knowledge, that the information
          provided above is complete and correct
        </p>
        <CTAButton
          buttonClass="w-3/4 mobile:w-2/3 mx-auto md:w-full bg-primary-blue font-avenir-black tracking-wider text-white rounded-full uppercase py-3 px-6 text-sm md:text-base inline-block hover:bg-navy-blue flex justify-center items-center"
          buttonText="Next"
          disabled={isLoading}
          isLoading={isLoading}
          onClick={save}
        />
      </div>
    </div>
  );
}
